import { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { Activity, Ticket as TicketIcon, CheckCircle2, Trophy, FileDown } from "lucide-react";
import { WinnerTrophy } from "@/components/WinnerTrophy";

const PERIODS = [{ k: "7", label: "7d" }, { k: "30", label: "30d" }, { k: "90", label: "90d" }, { k: "month", label: "This month" }];

const periodParams = (k) => {
  if (k !== "month") { return { days: k }; }
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

const fmtActive = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

const Board = ({ title, icon, rows, valueKey, fmt, testid }) => {
  const max = Math.max(1, ...rows.map((r) => r[valueKey]));
  return (
    <div className="border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">{title}</div>
      </div>
      {rows.length === 0 && <div className="text-xs text-slate-500 py-4">No activity in this period.</div>}
      <ol className="space-y-3">
        {rows.map((r, i) => (
          <li key={r.user_id} data-testid={`${testid}-row-${i}`}>
            <div className="flex items-center gap-2 text-sm">
              <span className={`h-5 w-5 shrink-0 rounded-sm flex items-center justify-center text-[10px] font-mono ${i === 0 ? "bg-red-600 text-white" : "bg-slate-100 text-slate-600"}`}>{i + 1}</span>
              <span className="flex-1 min-w-0 flex items-center gap-1"><span className="truncate">{r.name}</span><WinnerTrophy userId={r.user_id} /></span>
              <span className="font-mono text-xs text-slate-900" data-testid={`${testid}-value-${i}`}>{fmt(r[valueKey])}</span>
            </div>
            <div className="mt-1.5 ml-7 h-1 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${i === 0 ? "bg-red-600" : "bg-slate-900"}`} style={{ width: `${(r[valueKey] / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

export const AgentLeaderboard = () => {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/dashboard/leaderboard", { params: periodParams(days) }).then(({ data: d }) => setData(d)).catch(() => setData(null));
  }, [days]);
  if (!data) { return null; }
  const pdfHref = `${API_BASE}/dashboard/leaderboard.pdf?${new URLSearchParams(periodParams(days)).toString()}`;
  return (
    <div className="space-y-3" data-testid="agent-leaderboard">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-red-600" strokeWidth={1.75} />
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Top agents</div>
        </div>
        <div className="flex flex-wrap gap-1" data-testid="leaderboard-period">
          {PERIODS.map((p) => (
            <button key={p.k} onClick={() => setDays(p.k)} data-testid={`leaderboard-period-${p.k}`} className={`px-2.5 h-7 text-[11px] font-mono rounded-sm border transition-colors ${days === p.k ? "bg-red-600 border-red-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-red-400"}`}>
              {p.label}
            </button>
          ))}
          <a href={pdfHref} data-testid="leaderboard-pdf-btn" className="inline-flex items-center gap-1 px-2.5 h-7 text-[11px] font-mono rounded-sm border border-slate-900 bg-slate-900 text-white hover:bg-red-600 hover:border-red-600 transition-colors">
            <FileDown className="h-3.5 w-3.5" strokeWidth={1.75} /> PDF
          </a>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Board title="Most active (time online)" icon={<Activity className="h-4 w-4 text-red-600" strokeWidth={1.75} />} rows={data.most_active} valueKey="active_minutes" fmt={fmtActive} testid="lb-active" />
        <Board title="Most tickets handled" icon={<TicketIcon className="h-4 w-4 text-slate-900" strokeWidth={1.75} />} rows={data.most_handled} valueKey="handled" fmt={(v) => v} testid="lb-handled" />
        <Board title="Most solved / closed" icon={<CheckCircle2 className="h-4 w-4 text-red-600" strokeWidth={1.75} />} rows={data.most_solved} valueKey="solved" fmt={(v) => v} testid="lb-solved" />
      </div>
    </div>
  );
};
